// Programación semanal de técnicos de mantenimiento (coordinador_mtto).
//
// Clon estructural de C3 (programación de supervisores) con las diferencias
// del contrato programacionTecnicosSemanaSchema:
//   - VARIOS técnicos por celda (fecha × turno) — chips apilados con X
//   - planta_id opcional por asignación (NULL = cubre todas las plantas)
//   - `puede_editar` del response manda: viewer/técnico ven el grid read-only
//     y el técnico ve resaltadas (anillo accent) las celdas donde está él
//   - Sin "Publicar" ni "Importar Excel" (no aplican al módulo)

import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import type {
  ProgramacionTecnicoEnriquecida,
  ProgramacionTecnicosSemana,
  TecnicoResumen,
} from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { useMe } from '../../lib/api/queries';
import { cn } from '../../lib/utils';
import { useEstructuraMtto } from './estructura';
import { CargandoModulo, ErrorModulo, MantenimientoShell } from './MantenimientoShell';
import {
  useBorrarProgramacionTecnico,
  useCopiarSemanaTecnicos,
  useCrearProgramacionTecnico,
  useProgramacionTecnicosSemana,
} from './queries';
import { esCodigoApi, mensajeError } from './shared';

// =============================================================================
// Helpers (mismos criterios que C3 — fechas planas en marco UTC, sin shift)
// =============================================================================

const FMT_DIA_SEMANA = new Intl.DateTimeFormat('es-CO', { weekday: 'short', timeZone: 'UTC' });

/** Nombre corto capitalizado del día de una fecha ISO ('2026-06-29' → 'Lun').
 *  Deriva del día REAL, así el grid funciona con cualquier inicio de semana. */
function diaSemanaCorto(fechaIso: string): string {
  if (!fechaIso || fechaIso.length < 10) return '';
  const [y, m, d] = fechaIso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  const corto = FMT_DIA_SEMANA.format(new Date(Date.UTC(y, m - 1, d))).replace(/\./g, '');
  return corto.charAt(0).toUpperCase() + corto.slice(1);
}

function formatearFechaCorta(fechaIso: string): string {
  if (!fechaIso || fechaIso.length < 10) return '';
  const parts = fechaIso.slice(0, 10).split('-').map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return '';
  const date = new Date(Date.UTC(y, m - 1, d));
  const fmt = new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return fmt.format(date).replace(/\.|,/g, '').replace(/\s+/g, ' ').trim();
}

function formatearFechaLarga(fechaIso: string): string {
  if (!fechaIso || fechaIso.length < 10) return '';
  const parts = fechaIso.slice(0, 10).split('-').map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return '';
  const date = new Date(Date.UTC(y, m - 1, d));
  const fmt = new Intl.DateTimeFormat('es-CO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return fmt.format(date).replace(/\.|,/g, '').replace(/\s+/g, ' ').trim();
}

/** Semana ISO desplazada N semanas ('2026-W27' + 1 → '2026-W28'). */
function semanaIsoOffset(iso: string, deltaSemanas: number): string {
  const m = iso.match(/^(\d{4})-W(\d{2})$/);
  if (!m) return iso;
  const año = Number(m[1]);
  const semana = Number(m[2]);
  const ene4 = new Date(Date.UTC(año, 0, 4));
  const dow = ene4.getUTCDay() || 7;
  const lunesSem1 = new Date(ene4);
  lunesSem1.setUTCDate(ene4.getUTCDate() - (dow - 1));
  const lunes = new Date(lunesSem1);
  lunes.setUTCDate(lunesSem1.getUTCDate() + (semana - 1 + deltaSemanas) * 7);
  const tmp = new Date(Date.UTC(lunes.getUTCFullYear(), lunes.getUTCMonth(), lunes.getUTCDate()));
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const wn = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${tmp.getUTCFullYear()}-W${String(wn).padStart(2, '0')}`;
}

function inicialesDe(nombre: string, apellido: string): string {
  return `${nombre[0] ?? ''}${apellido[0] ?? ''}`.toUpperCase();
}

/** "Andrea Gómez" → "Andrea G." (chips compactos dentro de la celda). */
function nombreCorto(nombre: string, apellido: string): string {
  const inicialApellido = apellido[0] ? ` ${apellido[0].toUpperCase()}.` : '';
  return `${nombre}${inicialApellido}`;
}

// =============================================================================
// Componente principal
// =============================================================================

export function ProgramacionTecnicosPage() {
  const [semanaIso, setSemanaIso] = useState<string | undefined>(undefined);
  const [plantaId, setPlantaId] = useState<string>('');
  const { data, isLoading, isError, error } = useProgramacionTecnicosSemana(
    semanaIso,
    plantaId || undefined,
  );
  const estructura = useEstructuraMtto();
  const me = useMe();
  // turno_id = fila canónica deduplicada del response (es el id para CREAR);
  // turno_numero = clave real de la celda — asignaciones que referencian el
  // turno homólogo de otra planta caen en la misma fila del grid.
  const [modalCelda, setModalCelda] = useState<{
    fecha: string;
    turno_id: string;
    turno_numero: number;
  } | null>(null);

  const copiarMut = useCopiarSemanaTecnicos();

  const subtitulo = data
    ? `${data.tenant.nombre} · Semana ${data.semana_iso.split('-W')[1] ?? ''} · ${formatearFechaCorta(
        data.dias[0]!,
      )} → ${formatearFechaCorta(data.dias[6]!)}`
    : 'Cobertura semanal por turno';

  function handleCopiarSemanaAnterior() {
    if (!data) return;
    copiarMut.mutate(
      { semana_iso_destino: data.semana_iso },
      {
        onSuccess: (r) =>
          toast.success(`Copiadas ${r.filas_creadas} asignaciones desde ${r.semana_iso_origen}`),
        onError: (err) => toast.error(mensajeError(err)),
      },
    );
  }

  return (
    <MantenimientoShell titulo="Programación de técnicos" subtitulo={subtitulo}>
      {isLoading ? <CargandoModulo mensaje="Cargando programación…" /> : null}
      {isError ? <ErrorModulo error={error} /> : null}

      {data ? (
        <div className="flex flex-col gap-4">
          <HeaderRibbon
            data={data}
            onSemanaIsoChange={(iso) => setSemanaIso(iso ?? undefined)}
            onCopiarSemanaAnterior={handleCopiarSemanaAnterior}
            isCopiando={copiarMut.isPending}
            plantaId={plantaId}
            onPlantaChange={setPlantaId}
            plantas={estructura.plantas}
          />

          {data.tecnicos.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center text-sm text-fg-tertiary">
                No hay técnicos activos en el módulo. Solicite al coordinador de mantenimiento o al
                administrador que asigne usuarios con rol técnico en la pestaña Personal.
              </CardContent>
            </Card>
          ) : data.puede_editar ? (
            <>
              {/* Coordinador: listado de técnicos (balancea carga) + grid editable. */}
              <TecnicosRibbon data={data} />
              <GridSemanal
                data={data}
                miUserId={me.data?.ctx.user_id ?? null}
                onAgregar={(celda) => setModalCelda(celda)}
              />
            </>
          ) : (
            /* Técnico (solo lectura): su propia semana como lista clara — sin
               grid con scroll horizontal ni la carga de los compañeros. */
            <MiSemanaTecnico data={data} miUserId={me.data?.ctx.user_id ?? null} />
          )}

          {modalCelda ? (
            <ModalAsignarTecnico
              data={data}
              celda={modalCelda}
              onClose={() => setModalCelda(null)}
            />
          ) : null}

          <footer className="text-center text-[11px] text-fg-tertiary">
            {data.puede_editar
              ? `Semana ${data.semana_iso} · ${data.metricas.celdas_cubiertas}/${data.metricas.celdas_total} celdas cubiertas`
              : `Semana ${data.semana_iso}`}
          </footer>
        </div>
      ) : null}
    </MantenimientoShell>
  );
}

// =============================================================================
// HeaderRibbon — navegación de semanas + métricas inline + copiar semana
// =============================================================================

function HeaderRibbon({
  data,
  onSemanaIsoChange,
  onCopiarSemanaAnterior,
  isCopiando,
  plantaId,
  onPlantaChange,
  plantas,
}: {
  data: ProgramacionTecnicosSemana;
  onSemanaIsoChange: (iso: string | null) => void;
  onCopiarSemanaAnterior: () => void;
  isCopiando: boolean;
  plantaId: string;
  onPlantaChange: (id: string) => void;
  plantas: Array<{ id: string; nombre: string }>;
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-border pb-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
          {/* Métricas de cobertura global: solo al coordinador (gestión). */}
          {data.puede_editar ? (
            <>
              <MetricaInline
                label="Cubiertas"
                value={`${data.metricas.celdas_cubiertas} / ${data.metricas.celdas_total}`}
                tone={
                  data.metricas.celdas_cubiertas === data.metricas.celdas_total ? 'ok' : 'neutral'
                }
              />
              <MetricaInline
                label="Sin cubrir"
                value={String(data.metricas.sin_cubrir_count)}
                tone={data.metricas.sin_cubrir_count > 0 ? 'warn' : 'ok'}
              />
            </>
          ) : null}
          <label className="flex items-center gap-2 text-[11px] text-fg-tertiary">
            <span className="uppercase tracking-wide">Planta</span>
            <select
              value={plantaId}
              onChange={(e) => onPlantaChange(e.target.value)}
              className="rounded border border-border bg-surface-base px-2 py-1 text-xs text-fg"
            >
              <option value="">Todas las plantas</option>
              {plantas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSemanaIsoChange(semanaIsoOffset(data.semana_iso, -1))}
          >
            ← Anterior
          </Button>
          <Button variant="outline" size="sm" onClick={() => onSemanaIsoChange(null)}>
            Esta semana
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSemanaIsoChange(semanaIsoOffset(data.semana_iso, 1))}
          >
            Siguiente →
          </Button>
          {data.puede_editar ? (
            <Button
              variant="outline"
              size="sm"
              disabled={!data.semana_anterior_tiene_data || isCopiando}
              onClick={onCopiarSemanaAnterior}
            >
              {isCopiando ? 'Copiando…' : 'Copiar semana anterior'}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function MetricaInline({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'ok' | 'crit' | 'warn' | 'neutral';
}) {
  const toneCls =
    tone === 'ok'
      ? 'text-ok'
      : tone === 'crit'
        ? 'text-crit'
        : tone === 'warn'
          ? 'text-warn'
          : 'text-fg';
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        {label}
      </span>
      <span className={cn('text-sm font-medium tabular-nums', toneCls)}>{value}</span>
    </div>
  );
}

// =============================================================================
// TecnicosRibbon — chips horizontales con avatar + carga semanal
// =============================================================================

function TecnicosRibbon({ data }: { data: ProgramacionTecnicosSemana }) {
  return (
    <ul
      className="flex flex-wrap items-center gap-2"
      aria-label="Técnicos activos esta semana"
      role="list"
    >
      {data.tecnicos.map((t) => {
        const turnos = data.turnos_por_tecnico[t.id] ?? 0;
        return (
          <li
            key={t.id}
            className="flex items-center gap-2 rounded-md border border-border bg-surface-base px-2.5 py-1.5"
          >
            <span className="flex size-7 flex-shrink-0 items-center justify-center rounded-full bg-surface-overlay text-[11px] font-semibold text-fg">
              {t.iniciales}
            </span>
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-[13px] font-medium text-fg">
                {t.nombre} {t.apellido}
              </span>
              <span className="text-[10.5px] text-fg-tertiary">
                <span className="tabular-nums">{turnos}</span> turno{turnos === 1 ? '' : 's'} esta
                semana
                {t.planta_nombre ? ` · ${t.planta_nombre}` : ' · Todas las plantas'}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// =============================================================================
// MiSemanaTecnico — vista de SOLO LECTURA para el técnico: sus turnos de la
// semana como lista por día (clara en el teléfono, sin scroll horizontal ni
// la carga de los compañeros).
// =============================================================================

function MiSemanaTecnico({
  data,
  miUserId,
}: {
  data: ProgramacionTecnicosSemana;
  miUserId: string | null;
}) {
  const turnoPorNumero = new Map(data.turnos.map((t) => [t.numero, t]));
  const misTurnosPorDia = new Map<string, number[]>();
  for (const p of data.programaciones) {
    if (p.tecnico_user_id !== miUserId) continue;
    const arr = misTurnosPorDia.get(p.fecha) ?? [];
    if (!arr.includes(p.turno_numero)) arr.push(p.turno_numero);
    misTurnosPorDia.set(p.fecha, arr);
  }
  const totalMisTurnos = [...misTurnosPorDia.values()].reduce((s, a) => s + a.length, 0);

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-[11px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
          Mis turnos de la semana
        </h2>
        <span className="text-[11px] text-fg-tertiary">
          {totalMisTurnos} {totalMisTurnos === 1 ? 'turno' : 'turnos'}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {data.dias.map((fecha) => {
          const nums = (misTurnosPorDia.get(fecha) ?? []).slice().sort((a, b) => a - b);
          const esHoy = fecha === data.fecha_hoy;
          return (
            <div
              key={fecha}
              className={cn(
                'flex items-center justify-between gap-3 rounded-lg border px-4 py-3',
                esHoy ? 'border-accent bg-accent-soft/30' : 'border-border bg-surface-elevated',
              )}
            >
              <span className="text-sm font-medium capitalize text-fg">
                {diaSemanaCorto(fecha)} {formatearFechaCorta(fecha)}
                {esHoy ? <span className="ml-1 text-accent">· hoy</span> : null}
              </span>
              <div className="flex flex-wrap justify-end gap-1.5">
                {nums.length === 0 ? (
                  <span className="text-xs text-fg-tertiary">Sin turno</span>
                ) : (
                  nums.map((n) => {
                    const t = turnoPorNumero.get(n);
                    return (
                      <span
                        key={n}
                        className="rounded bg-accent/15 px-2 py-1 text-xs font-medium text-accent"
                      >
                        T{n}
                        {t ? ` · ${t.hora_inicio.slice(0, 5)}–${t.hora_fin.slice(0, 5)}` : ''}
                      </span>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// =============================================================================
// GridSemanal — filas turno × columnas días, VARIOS técnicos por celda
// =============================================================================

function GridSemanal({
  data,
  miUserId,
  onAgregar,
}: {
  data: ProgramacionTecnicosSemana;
  miUserId: string | null;
  onAgregar: (celda: { fecha: string; turno_id: string; turno_numero: number }) => void;
}) {
  // Celdas por (fecha, turno_numero) — NO por turno_id: cada planta tiene su
  // propia fila de turno en BD y los homólogos deben caer en la misma celda.
  const porCelda = useMemo(() => {
    const m = new Map<string, ProgramacionTecnicoEnriquecida[]>();
    for (const p of data.programaciones) {
      const key = `${p.fecha}|${p.turno_numero}`;
      const lista = m.get(key);
      if (lista) lista.push(p);
      else m.set(key, [p]);
    }
    return m;
  }, [data.programaciones]);

  const turnosOrdenados = useMemo(
    () => [...data.turnos].sort((a, b) => a.numero - b.numero),
    [data.turnos],
  );

  return (
    <Card>
      {/* max-h + overflow-auto: el scroll vertical vive en el contenedor para
          que el thead sticky mantenga los días visibles (mismo patrón que la
          matriz del detalle C4). */}
      <CardContent className="max-h-[calc(100vh-180px)] overflow-auto p-0">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 z-20">
            <tr className="bg-surface-overlay">
              <th className="sticky left-0 z-30 border-b border-r border-border bg-surface-overlay px-3 py-2 text-left">
                <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                  Turno
                </span>
              </th>
              {data.dias.map((fecha) => (
                <th
                  key={fecha}
                  className="border-b border-l border-border bg-surface-overlay px-2 py-2 text-center"
                >
                  <div className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                    {diaSemanaCorto(fecha)} {fecha.slice(8, 10)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {turnosOrdenados.map((t) => (
              <tr key={t.id}>
                <th
                  scope="row"
                  className="sticky left-0 z-10 border-b border-r border-border bg-surface-base px-3 py-2 text-left align-top"
                >
                  <div className="text-sm font-medium text-fg">T{t.numero}</div>
                  <div className="font-mono text-[10.5px] text-fg-tertiary">
                    {t.hora_inicio.slice(0, 5)}–{t.hora_fin.slice(0, 5)}
                  </div>
                </th>
                {data.dias.map((fecha) => {
                  const key = `${fecha}|${t.numero}`;
                  const progs = porCelda.get(key) ?? [];
                  const esPasada = fecha < data.fecha_hoy;
                  return (
                    <td
                      key={key}
                      className={cn(
                        'border-b border-l border-border p-1.5 align-top',
                        esPasada && 'bg-surface-overlay/40',
                      )}
                    >
                      <CeldaTecnicos
                        progs={progs}
                        esPasada={esPasada}
                        puedeEditar={data.puede_editar}
                        miUserId={miUserId}
                        onAgregar={() =>
                          onAgregar({ fecha, turno_id: t.id, turno_numero: t.numero })
                        }
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function CeldaTecnicos({
  progs,
  esPasada,
  puedeEditar,
  miUserId,
  onAgregar,
}: {
  progs: ProgramacionTecnicoEnriquecida[];
  esPasada: boolean;
  puedeEditar: boolean;
  miUserId: string | null;
  onAgregar: () => void;
}) {
  const borrar = useBorrarProgramacionTecnico();
  // En modo lectura (viewer / técnico) se resalta la celda donde está el
  // usuario actual — el técnico VE su semana de un vistazo.
  const soyYo =
    !puedeEditar && miUserId !== null && progs.some((p) => p.tecnico_user_id === miUserId);

  function handleQuitar(p: ProgramacionTecnicoEnriquecida) {
    borrar.mutate(p.id, {
      onSuccess: () => toast.success(`${p.tecnico_nombre} ${p.tecnico_apellido} quitado del turno`),
      onError: (err) => toast.error(mensajeError(err)),
    });
  }

  if (progs.length === 0 && esPasada) {
    return (
      <div className="flex w-full items-center justify-center rounded-md border border-dashed border-border px-2 py-2 text-[11px] text-fg-tertiary/40">
        —
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex min-h-9 w-full flex-col gap-1 rounded-md p-0.5',
        soyYo && 'ring-2 ring-accent',
      )}
    >
      {progs.length === 0 && !puedeEditar ? (
        <div className="flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-warn/40 px-2 py-2 text-[11px] text-warn">
          <span aria-hidden>⚠</span>
          <span>Sin cubrir</span>
        </div>
      ) : null}

      {progs.map((p) => {
        const puedeQuitar = puedeEditar && !esPasada;
        return (
          <span
            key={p.id}
            className={cn(
              'flex w-full items-center gap-1.5 rounded-md border border-border bg-surface-base px-1.5 py-1 text-left',
              esPasada && 'opacity-60',
            )}
          >
            <span className="flex size-5 flex-shrink-0 items-center justify-center rounded-full bg-surface-overlay text-[9px] font-semibold text-fg">
              {inicialesDe(p.tecnico_nombre, p.tecnico_apellido)}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-[11px] font-medium text-fg">
                {nombreCorto(p.tecnico_nombre, p.tecnico_apellido)}
              </span>
              {p.planta_nombre ? (
                <span className="truncate text-[9.5px] text-fg-tertiary">{p.planta_nombre}</span>
              ) : null}
            </span>
            {puedeQuitar ? (
              <button
                type="button"
                aria-label={`Quitar a ${p.tecnico_nombre} ${p.tecnico_apellido}`}
                disabled={borrar.isPending}
                onClick={() => handleQuitar(p)}
                className="flex size-4 flex-shrink-0 items-center justify-center rounded text-fg-tertiary transition-colors hover:bg-crit-soft hover:text-crit disabled:opacity-50"
              >
                ×
              </button>
            ) : null}
          </span>
        );
      })}

      {puedeEditar && !esPasada ? (
        <button
          type="button"
          onClick={onAgregar}
          className={cn(
            'flex w-full items-center justify-center gap-1 rounded-md border border-dashed px-2 py-1.5 text-[11px] transition-colors',
            progs.length === 0
              ? 'border-warn/40 text-warn hover:bg-warn-soft/30'
              : 'border-border text-fg-tertiary hover:border-fg-tertiary hover:text-fg',
          )}
        >
          {progs.length === 0 ? (
            <>
              <span aria-hidden>⚠</span>
              <span>Sin cubrir · agregar</span>
            </>
          ) : (
            '+ Agregar'
          )}
        </button>
      ) : null}
    </div>
  );
}

// =============================================================================
// ModalAsignarTecnico — lista de cards con carga semanal + planta opcional
// =============================================================================

function ModalAsignarTecnico({
  data,
  celda,
  onClose,
}: {
  data: ProgramacionTecnicosSemana;
  celda: { fecha: string; turno_id: string; turno_numero: number };
  onClose: () => void;
}) {
  const turno = data.turnos.find((t) => t.id === celda.turno_id);
  const estructura = useEstructuraMtto();

  // Técnicos que YA están asignados a esta celda — se marcan y no se re-crean.
  // Por (fecha, turno_numero): una asignación al turno homólogo de otra planta
  // es la MISMA celda operativa.
  const yaAsignados = useMemo(() => {
    const set = new Set<string>();
    for (const p of data.programaciones) {
      if (p.fecha === celda.fecha && p.turno_numero === celda.turno_numero)
        set.add(p.tecnico_user_id);
    }
    return set;
  }, [data.programaciones, celda]);

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string>('');
  const [plantaId, setPlantaId] = useState<string>('');

  // Opciones de planta: catálogo del módulo; si aún no cargó, se derivan de
  // las plantas base de los técnicos (mejor que un select vacío).
  const plantas = useMemo(() => {
    if (estructura.plantas.length > 0) return estructura.plantas;
    const m = new Map<string, string>();
    for (const t of data.tecnicos) {
      if (t.planta_id && t.planta_nombre) m.set(t.planta_id, t.planta_nombre);
    }
    return [...m.entries()].map(([id, nombre]) => ({ id, nombre }));
  }, [estructura.plantas, data.tecnicos]);

  const tecnicosFiltrados = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return data.tecnicos;
    return data.tecnicos.filter(
      (t) =>
        t.nombre.toLowerCase().includes(s) ||
        t.apellido.toLowerCase().includes(s) ||
        t.email.toLowerCase().includes(s),
    );
  }, [data.tecnicos, search]);

  const crear = useCrearProgramacionTecnico();

  async function handleAgregar() {
    if (!selectedId) {
      toast.error('Elija un técnico');
      return;
    }
    try {
      await crear.mutateAsync({
        fecha: celda.fecha,
        turno_id: celda.turno_id,
        tecnico_user_id: selectedId,
        planta_id: plantaId === '' ? null : plantaId,
      });
      toast.success('Técnico programado');
      onClose();
    } catch (err) {
      if (esCodigoApi(err, 'duplicate')) {
        toast.error('Ese técnico ya está en ese turno');
        return;
      }
      if (esCodigoApi(err, 'date_in_past')) {
        toast.error(mensajeError(err));
        return;
      }
      toast.error(mensajeError(err));
    }
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Agregar técnico al turno</DialogTitle>
          <p className="mt-1 text-[11px] text-fg-tertiary">
            {formatearFechaLarga(celda.fecha)} ·{' '}
            <span className="font-mono">
              T{celda.turno_numero} · {turno?.hora_inicio.slice(0, 5) ?? '--'}–
              {turno?.hora_fin.slice(0, 5) ?? '--'}
            </span>
          </p>
        </DialogHeader>

        <Input
          placeholder="Buscar técnico…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />

        <ul className="flex max-h-[280px] flex-col gap-1.5 overflow-y-auto pr-1" role="list">
          {tecnicosFiltrados.length === 0 ? (
            <li className="py-4 text-center text-[12px] text-fg-tertiary">Sin resultados</li>
          ) : (
            tecnicosFiltrados.map((tec) => {
              const turnos = data.turnos_por_tecnico[tec.id] ?? 0;
              const asignado = yaAsignados.has(tec.id);
              const isSelected = selectedId === tec.id;
              return (
                <li key={tec.id}>
                  <button
                    type="button"
                    disabled={asignado}
                    onClick={() => setSelectedId(tec.id)}
                    onDoubleClick={() => {
                      if (asignado) return;
                      setSelectedId(tec.id);
                      void handleAgregar();
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md border px-2.5 py-2 text-left transition-colors',
                      asignado
                        ? 'cursor-not-allowed border-border bg-surface-overlay/50 opacity-60'
                        : isSelected
                          ? 'border-accent bg-accent-soft/40'
                          : 'border-border bg-surface-base hover:border-fg-tertiary',
                    )}
                  >
                    <TecnicoAvatar tecnico={tec} />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-baseline gap-2">
                        <span className="truncate text-[13px] font-medium text-fg">
                          {tec.nombre} {tec.apellido}
                        </span>
                        {asignado ? (
                          <Badge tone="neutral" className="text-[9px]">
                            ya en este turno
                          </Badge>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-baseline gap-1.5 text-[10.5px] text-fg-tertiary">
                        <span>
                          <span className="tabular-nums">{turnos}</span> turno
                          {turnos === 1 ? '' : 's'} esta semana
                        </span>
                        <span>· {tec.planta_nombre ?? 'Todas las plantas'}</span>
                      </div>
                    </div>
                    {isSelected && !asignado ? (
                      <span aria-hidden className="text-accent">
                        ✓
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })
          )}
        </ul>

        <div className="flex flex-col gap-1">
          <Label htmlFor="prog-tec-planta">Planta que cubre</Label>
          <Select
            id="prog-tec-planta"
            value={plantaId}
            onChange={(e) => setPlantaId(e.target.value)}
          >
            <option value="">Todas las plantas</option>
            {plantas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
              </option>
            ))}
          </Select>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={crear.isPending || !selectedId}
            onClick={() => {
              void handleAgregar();
            }}
          >
            {crear.isPending ? 'Agregando…' : 'Agregar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TecnicoAvatar({ tecnico }: { tecnico: TecnicoResumen }) {
  return (
    <span className="flex size-8 flex-shrink-0 items-center justify-center rounded-full bg-surface-overlay text-[11px] font-semibold text-fg">
      {tecnico.iniciales}
    </span>
  );
}
